/** @format */

import CloseIcon from '@mui/icons-material/Close';
import {Backdrop, Box, IconButton, Typography} from '@mui/material';
import {styled} from '@mui/material/styles';

import {scrollbarStyles} from 'constants/scrollbar';

const PREFIX = 'Modal';

const classes = {
  backdropNew: `${PREFIX}-backdropNew`,
  outerContainer: `${PREFIX}-outerContainer`,
  outerOuterContainerNew: `${PREFIX}-outerOuterContainerNew`,
  innerContainerNew: `${PREFIX}-innerContainerNew`,
  closeIconNew: `${PREFIX}-closeIconNew`,
  header: `${PREFIX}-header`,
  headerNew: `${PREFIX}-headerNew`,
  headerText: `${PREFIX}-headerText`,
  contentNew: `${PREFIX}-contentNew`,
};

const getBackgroundColor = (variant: ModalVariant) => {
  if (variant === 'light-secondary') {
    return '#E3E3E3';
  }
  return variant === 'dark' ? '#202020' : '#FFFFFF';
};

const StyledBackdrop = styled(Backdrop)<{variant: ModalVariant}>(({theme, variant}) => {
  const backgroundColor = getBackgroundColor(variant);
  const textColor = variant === 'dark' ? theme.palette.text.primary : theme.palette.text.secondary;

  return {
    [`&.${classes.backdropNew}`]: {
      backgroundColor: 'rgba(17, 17, 17, 0.9)',
      backdropFilter: 'blur(2px)',
      zIndex: theme.zIndex.drawer + 1,
    },

    // https://front-back.com/how-to-make-absolute-positioned-elements-overlap-their-overflow-hidden-parent/
    //
    // Outer container is used as a relative parent for close icon.
    // Inner container is used to crop content (with border radius).
    //
    // Inner container can't be used as relative parent for close icon since it
    // would hide the icon because of `overflow: hidden` property.
    [`& .${classes.outerContainer}`]: {
      minWidth: 350,
      [theme.breakpoints.down('sm')]: {
        alignSelf: 'stretch',
        flexGrow: 1,
      },
      [theme.breakpoints.up('sm')]: {
        alignSelf: 'center',
      },
    },

    [`& .${classes.outerOuterContainerNew}`]: {
      // Set max width for `overflow-x: scroll` to work
      maxWidth: '100vw',
      minWidth: 350,
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: '16px',
      backgroundColor: backgroundColor,
      [theme.breakpoints.down('sm')]: {
        alignSelf: 'stretch',
        flexGrow: 1,
        height: '100%',
      },
      [theme.breakpoints.up('sm')]: {
        alignSelf: 'center',
      },
    },

    [`& .${classes.innerContainerNew}`]: {
      backgroundColor: backgroundColor,
      overflow: 'auto',
      ...scrollbarStyles(theme, variant === 'dark' ? 'dark' : 'light'),
      [theme.breakpoints.down('sm')]: {
        height: '100%',
      },
      [theme.breakpoints.up('sm')]: {
        maxHeight: 584,
        maxWidth: 700,
      },
    },

    [`& .${classes.closeIconNew}`]: {
      color: textColor,
      position: 'absolute',
      right: theme.spacing(5),
      top: theme.spacing(5),
    },

    [`& .${classes.header}`]: {
      paddingBottom: theme.spacing(4),
      [theme.breakpoints.down('sm')]: {
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
        paddingTop: theme.spacing(7),
      },
      [theme.breakpoints.up('sm')]: {
        paddingLeft: theme.spacing(6),
        paddingRight: theme.spacing(12),
        paddingTop: theme.spacing(5),
        maxWidth: 700,
      },
    },

    [`& .${classes.headerNew}`]: {
      padding: theme.spacing(8),
      paddingRight: theme.spacing(14),
      color: textColor,
      fontSize: '24px',
      fontWeight: 700,
      lineHeight: 'normal',
    },

    [`& .${classes.headerText}`]: {
      color: theme.palette.text.secondary,
      fontSize: 20,
      fontWeight: 400,
      lineHeight: '23px',
    },

    [`& .${classes.contentNew}`]: {
      color: textColor,
      padding: theme.spacing(8),
      paddingTop: 0,
      paddingBottom: 0,
    },
  };
});

export type ModalVariant = 'light' | 'dark' | 'light-secondary';

interface Props {
  header?: string | React.ReactNode;
  footer?: string | React.ReactNode;
  children: React.ReactNode;
  variant?: ModalVariant;
  onClose?: () => void;
  showCloseIcon?: boolean;
}

// https://reactrouter.com/web/example/modal-gallery
const Modal = (props: Props) => {
  const handleClose = (event: React.SyntheticEvent) => {
    event.stopPropagation();

    if (props.onClose) {
      props.onClose();
    }
  };

  return (
    <StyledBackdrop
      className={classes.backdropNew}
      open
      transitionDuration={50}
      variant={props.variant || 'light'}
    >
      <Box className={classes.outerContainer}>
        <Box className={classes.outerOuterContainerNew}>
          <Box className={classes.headerNew}>
            {typeof props.header === 'string' ? (
              <Typography>{props.header}</Typography>
            ) : (
              props.header
            )}
          </Box>
          <Box className={classes.innerContainerNew}>
            {props.showCloseIcon && (
              <IconButton className={classes.closeIconNew} onClick={handleClose} size='large'>
                <CloseIcon fontSize='small' />
              </IconButton>
            )}

            <Box className={classes.contentNew}>{props.children}</Box>
          </Box>
          <Box className={classes.header}>
            {typeof props.footer === 'string' ? (
              <Typography className={classes.headerText}>{props.footer}</Typography>
            ) : (
              props.footer
            )}
          </Box>
        </Box>
      </Box>
    </StyledBackdrop>
  );
};

Modal.defaultProps = {
  variant: 'light',
  onClose: null,
  showCloseIcon: true,
};

export default Modal;
